import React, { useState, useEffect } from "react";
import { LogoutAdmin } from "../services/authApi";
import { useProfile } from "../hooks/useProfile";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  CssBaseline,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  SwipeableDrawer,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpen from "@mui/icons-material/MenuOpen";
import {
  Logout,
  ExpandLess,
  ExpandMore,
  PeopleAltOutlined,
  LocalPostOfficeOutlined,
  AccountTreeOutlined,
  VideoCameraFrontOutlined,
  EmojiEventsOutlined,
  NewspaperOutlined,
  FestivalOutlined,
  ConfirmationNumberOutlined,
  PaymentsOutlined,
  SettingsOutlined,
  SecurityOutlined,
} from "@mui/icons-material";
import { useAdminMode } from "../../adminTheme";
import { ThemeProvider } from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";

const drawerWidth = 260;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "ismobile",
})(({ theme, open, ismobile }) => ({
  zIndex: theme.zIndex.drawer + 1,
  width: ismobile === "true" ? "100%" : open ? `calc(100% - ${drawerWidth}px)` : "100%",
  marginLeft: ismobile === "true" ? 0 : open ? drawerWidth : 0,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(true);
  const [expandedItems, setExpandedItems] = useState(() => {
    // Auto-expand any menu group whose sub-path is a prefix of the current pathname
    const initial = {};
    const checkItems = [
      { text: "Festivals", path: "/festivals", subItems: [
        { path: "/access-control" }, { path: "/claims" }, { path: "/payments" }
      ]},
    ];
    checkItems.forEach((item) => {
      if (item.subItems?.some((sub) => window.location.pathname.startsWith(`${item.path}${sub.path}`))) {
        initial[item.text] = true;
      }
    });
    return initial;
  });

  // State for the floating submenu
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);

  const { profile, loading } = useProfile();
  const [adminTheme] = useAdminMode();
  const ismobile = useMediaQuery(adminTheme.breakpoints.down("sm"));

  useEffect(() => {
    if (ismobile) {
      setOpen(false);
    }
  }, [ismobile]);

  const handleLogout = async () => {
    try {
      await LogoutAdmin();
      localStorage.removeItem("token");
      navigate("/login");
    } catch (error) {
      console.error(error);
    }
  };

  const handleDrawerToggle = () => setOpen((prevOpen) => !prevOpen);
  const handleDrawerClose = () => { if (ismobile) setOpen(false); };
  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (ismobile) handleDrawerClose();
    handleSubMenuClose();
  };

  const handleSubMenuClose = () => {
    setSubMenuAnchorEl(null);
    setActiveSubMenu(null);
  };

  const handleItemInteraction = (event, item) => {
    if (item.subItems) {
      if (open) {
        setExpandedItems((prev) => ({ ...prev, [item.text]: !prev[item.text] }));
      } else {
        setSubMenuAnchorEl(event.currentTarget);
        setActiveSubMenu(item);
      }
    } else {
      handleMenuItemClick(item.path);
    }
  };

  const menuItems = [
    { text: "Users", path: "/users", icon: <PeopleAltOutlined /> },
    { text: "All Posts", path: "/posts", icon: <LocalPostOfficeOutlined /> },
    { text: "Projects", path: "/projects", icon: <AccountTreeOutlined /> },
    { text: "Jobs", path: "/jobs", icon: <VideoCameraFrontOutlined /> },
    { text: "Competitions", path: "/competitions", icon: <EmojiEventsOutlined /> },
    { text: "Newsletter", path: "/newsletter", icon: <NewspaperOutlined /> },
    {
      text: "Festivals",
      path: "/festivals",
      icon: <FestivalOutlined />,
      subItems: [
        { text: "SaaS & Access Control", path: "/access-control", icon: <SecurityOutlined /> },
        { text: "Claims", path: "/claims", icon: <ConfirmationNumberOutlined /> },
        { text: "Festival Payments", path: "/payments", icon: <PaymentsOutlined /> },
      ],
    },
    { text: "Finance & Tax", path: "/finance-tax", icon: <PaymentsOutlined /> },
  ];

  // CHANGED: Changed from Component to a function returning JSX
  const renderDrawerContent = () => (
    <>
      <Toolbar sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        {(!ismobile || open) && <LogoAndTitle />}
      </Toolbar>
      <Box sx={{ px: 1.5, py: 1 }}>
        <List sx={{ pt: 0 }}>
          {menuItems.map((item) => {
            const isExactActive = location.pathname === item.path;
            const isChildActive = item.subItems?.some(sub =>
              location.pathname.startsWith(`${item.path}${sub.path}`)
            );
            const isActive = isExactActive || isChildActive;

            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip
                    title={open ? "" : item.text}
                    placement="right"
                    arrow
                    disableHoverListener={open || ismobile}
                  >
                    <ListItemButton
                      selected={isActive}
                      onClick={(e) => handleItemInteraction(e, item)}
                      onMouseEnter={(e) => {
                        if (!open && !ismobile && item.subItems) {
                          handleItemInteraction(e, item);
                        }
                      }}
                      sx={{
                        minHeight: 44,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        borderRadius: 2,
                        transition: "all 0.2s ease-in-out",
                        "&.Mui-selected": {
                          backgroundColor: adminTheme.palette.primary.main + "15",
                          color: adminTheme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: adminTheme.palette.primary.main + "25",
                          },
                          "& .MuiListItemIcon-root": {
                            color: adminTheme.palette.primary.main,
                          }
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 2 : "auto",
                          justifyContent: "center",
                          color: adminTheme.palette.text.secondary,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 500,
                          fontSize: "0.95rem"
                        }}
                        sx={{ opacity: open || ismobile ? 1 : 0, m: 0 }}
                      />
                      {item.subItems && (open || ismobile) && (
                        expandedItems[item.text] ?
                          <ExpandLess fontSize="small" color="action" /> :
                          <ExpandMore fontSize="small" color="action" />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>

                {item.subItems && open && (
                  <Collapse in={expandedItems[item.text]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mb: 0.5 }}>
                      {item.subItems.map((subItem) => {
                        const isSubActive = location.pathname.startsWith(`${item.path}${subItem.path}`);
                        return (
                          <ListItem key={subItem.text} disablePadding sx={{ pl: 1.5 }}>
                            <ListItemButton
                              selected={isSubActive}
                              onClick={() => handleMenuItemClick(`${item.path}${subItem.path}`)}
                              sx={{
                                minHeight: 40,
                                borderRadius: 2,
                                pl: 2,
                                pr: 1,
                                "&.Mui-selected": {
                                  backgroundColor: "transparent",
                                  color: adminTheme.palette.primary.main,
                                  "& .MuiListItemIcon-root": { color: adminTheme.palette.primary.main }
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 28, color: adminTheme.palette.text.disabled }}>
                                {React.cloneElement(subItem.icon, { sx: { fontSize: 20 } })}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.text}
                                primaryTypographyProps={{
                                  fontSize: "0.85rem",
                                  fontWeight: isSubActive ? 600 : 400,
                                  noWrap: true,
                                }}
                                sx={{ m: 0 }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </>
  );

  const LogoAndTitle = ({ sx = {} }) => (
    <Box sx={{ display: "flex", alignItems: "center", ...sx }}>
      <Box
        component="img"
        sx={{
          height: ismobile ? 25 : 30,
          width: "auto",
          maxWidth: ismobile ? 100 : 120,
        }}
        alt="Logo"
        src="/assets/logo-red-(512x110).png"
      />
    </Box>
  );

  return (
    <React.Fragment>
      <Box sx={{ display: "flex", bgcolor: adminTheme.palette.background.default, minHeight: '100vh' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          elevation={1}
          open={open}
          ismobile={ismobile.toString()}
          sx={{
            bgcolor: adminTheme.palette.background.paper,
            color: adminTheme.palette.text.primary,
            height: ismobile ? 64 : 70,
            borderBottom: `1px solid ${adminTheme.palette.divider}`,
          }}
        >
          <Toolbar sx={{ pr: ismobile ? 1 : 3, height: "100%" }}>
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{ marginRight: 2 }}
            >
              {open ? <MenuOpen /> : <MenuIcon />}
            </IconButton>

            {(ismobile || !open) && <LogoAndTitle sx={{ flexGrow: 1 }} />}
            <Box sx={{ flexGrow: ismobile || !open ? 0 : 1 }} />

            <IconButton onClick={handleAvatarClick} sx={{ ml: 1 }}>
              {loading ? (
                <Avatar sx={{ width: ismobile ? 38 : 44, height: ismobile ? 38 : 44 }}>...</Avatar>
              ) : profile?.image ? (
                <Avatar src={profile.image} alt={profile.name} sx={{ width: ismobile ? 38 : 44, height: ismobile ? 38 : 44 }} />
              ) : (
                <Avatar sx={{ width: ismobile ? 38 : 44, height: ismobile ? 38 : 44, bgcolor: adminTheme.palette.primary.main }}>
                  {profile?.initial}
                </Avatar>
              )}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* CHANGED: Swapped <DrawerContent /> to {renderDrawerContent()} */}
        {ismobile ? (
          <SwipeableDrawer
            anchor="left"
            open={open}
            onClose={handleDrawerClose}
            onOpen={handleDrawerToggle}
            PaperProps={{
              sx: {
                backgroundColor: adminTheme.palette.background.paper,
                width: drawerWidth,
                borderRight: `1px solid ${adminTheme.palette.divider}`,
              },
            }}
          >
            {renderDrawerContent()}
          </SwipeableDrawer>
        ) : (
          <Drawer
            variant="permanent"
            open={open}
            PaperProps={{
              sx: {
                backgroundColor: adminTheme.palette.background.paper,
                borderRight: `1px solid ${adminTheme.palette.divider}`,
                zIndex: 1200,
              },
            }}
          >
            {renderDrawerContent()}
          </Drawer>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            mt: { xs: 8, sm: 9 },
            width: "100%",
          }}
        >
          <ThemeProvider theme={adminTheme}>
            <Outlet />
          </ThemeProvider>
        </Box>

        <Menu
          anchorEl={subMenuAnchorEl}
          open={Boolean(subMenuAnchorEl) && !open}
          onClose={handleSubMenuClose}
          MenuListProps={{
            onMouseLeave: handleSubMenuClose,
          }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            elevation: 4,
            sx: {
              ml: 1, // Added a tiny margin so it doesn't overlap the rounded button perfectly
              minWidth: 160,
              borderRadius: 2,
              backgroundColor: adminTheme.palette.background.paper,
            },
          }}
        >
          {activeSubMenu?.subItems?.map((subItem) => {
            const isSubActive = location.pathname === `${activeSubMenu.path}${subItem.path}`;
            return (
              <MenuItem
                key={subItem.text}
                selected={isSubActive}
                onClick={() => handleMenuItemClick(`${activeSubMenu.path}${subItem.path}`)}
                sx={{
                  py: 1,
                  px: 2,
                  "&.Mui-selected": {
                    backgroundColor: adminTheme.palette.primary.main + "15",
                    color: adminTheme.palette.primary.main,
                    "& .MuiListItemIcon-root": { color: adminTheme.palette.primary.main }
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: adminTheme.palette.text.secondary }}>
                  {React.cloneElement(subItem.icon, { sx: { fontSize: 20 } })}
                </ListItemIcon>
                <Typography variant="body2" fontWeight={isSubActive ? 600 : 400}>
                  {subItem.text}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              overflow: "visible",
              backgroundColor: adminTheme.palette.background.paper,
              mt: 1.5,
              width: ismobile ? 280 : 300,
              borderRadius: 2,
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 18,
                width: 10,
                height: 10,
                bgcolor: adminTheme.palette.background.paper,
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem sx={{ py: 1.5, "&:hover": { bgcolor: "transparent" } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
              <Avatar
                src={profile?.image}
                sx={{ width: 44, height: 44, bgcolor: adminTheme.palette.primary.main }}
              >
                {!profile?.image && profile?.initial}
              </Avatar>
              <Box sx={{ overflow: "hidden" }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {loading ? "Loading..." : profile?.name || "Admin User"}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {loading ? "..." : profile?.email || "admin@example.com"}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem sx={{ display: "flex", justifyContent: "center", pb: 1, "&:hover": { bgcolor: "transparent" } }}>
            <Button
              variant="outlined"
              startIcon={<Logout />}
              color="error"
              fullWidth
              onClick={handleLogout}
            >
              Sign Out
            </Button>
          </MenuItem>
        </Menu>
      </Box>
    </React.Fragment>
  );
};

export default AdminDashboard;